/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the 'Software'), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  WAIT: 'wait',
  EXECUTE: 'execute',
  SUCCESS: 'success',
  SCU_UNMATCHED: 'scuUnmatched',
  EEPROM_NG: 'eepromNg',
  COMMUNICATION_NG: 'communicationNg',
  MATCH_NG: 'matchNg',
  COMMUNICATION_ERROR: 'communicationError',
  FAILED: 'failed',
};

export default {
  name: 'requirement084',
  data() {
    return {
      modal_requirement_084: false,
      phase: PHASE.WAIT,
      isLoading: false,
      systemId: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.SCU_UNMATCHED:
        case PHASE.EEPROM_NG:
        case PHASE.COMMUNICATION_NG:
        case PHASE.MATCH_NG:
        case PHASE.COMMUNICATION_ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.WAIT:
        case PHASE.EXECUTE:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 待機表示
      const confirmationTitle = 'message.common_title_confirmation';
      // 実行表示
      const executionTitle = 'individual.common_title_execution';
      // 停止表示
      const successTitle = 'message.common_title_success';
      // SCU非対応表示, EEPROM NG表示, 通信NG表示, 照合NG表示, 通信エラー表示,  共通エラー
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        wait: confirmationTitle,
        execute: executionTitle,
        success: successTitle,
        scuUnmatched: errorTitle,
        eepromNg: errorTitle,
        communicationNg: errorTitle,
        matchNg: errorTitle,
        communicationError: errorTitle,
        failed: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        wait: `${this.i18nWsKey}.REQ_WS_84_TXT_1`,
        execute: `${this.i18nWsKey}.REQ_WS_84_TXT_2`,
        success: `${this.i18nWsKey}.REQ_WS_84_TXT_3`,
        scuUnmatched: `${this.i18nWsKey}.REQ_WS_84_TXT_4`,
        eepromNg: `${this.i18nWsKey}.REQ_WS_84_TXT_5`,
        communicationNg: `${this.i18nWsKey}.REQ_WS_84_TXT_6`,
        matchNg: `${this.i18nWsKey}.REQ_WS_84_TXT_7`,
        communicationError: `${this.i18nWsKey}.REQ_WS_84_TXT_8`,
        failed: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      const showOkButtonMap = {
        wait: true,
        success: true,
        scuUnmatched: true,
        eepromNg: true,
        communicationNg: true,
        matchNg: true,
        communicationError: true,
        failed: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_084 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },


    /** 実行処理 */
    async executeProcess() {
      // 実行画面へ遷移
      this.phase = PHASE.EXECUTE;

      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'ok':
                // 停止画面へ遷移
                this.phase = PHASE.SUCCESS;
                break;
              case 'scu_unmatched':
                // SCU非対応表示画面へ遷移
                this.phase = PHASE.SCU_UNMATCHED;
                break;
              case 'eeprom_ng':
                // EEPROM NG表示画面へ遷移
                this.phase = PHASE.EEPROM_NG;
                break;
              case 'communication_ng':
                // 通信NG表示画面へ遷移
                this.phase = PHASE.COMMUNICATION_NG;
                break;
              case 'match_ng':
                // 照合NG表示画面へ遷移
                this.phase = PHASE.MATCH_NG;
                break;
              case 'ng':
              default:
                // 通信エラー画面へ遷移
                this.phase = PHASE.COMMUNICATION_ERROR;
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：通信エラー画面へ遷移
            this.phase = PHASE.COMMUNICATION_ERROR;
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_084 = false;
    },

    async onOkClicked() {
      switch (this.phase) {
        case PHASE.WAIT:
          await this.executeProcess();
          break;
        case PHASE.SUCCESS:
        case PHASE.SCU_UNMATCHED:
        case PHASE.EEPROM_NG:
        case PHASE.COMMUNICATION_NG:
        case PHASE.MATCH_NG:
        case PHASE.COMMUNICATION_ERROR:
        case PHASE.FAILED:
          await this.closeWizard();
          break;
        default:
          break;
      }
    },
  },

  async mounted() {
    this.init();
  },
};